import { Preloader } from "@smartsuite/react-ui/lib/components/utils/Preloader/Preloader";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchDocumentDesignById } from "../../api/client";
import type { DocumentDesign } from "../../api/types";
import { Canvas } from "../../canvas/Canvas";
import type { CanvasManager } from "../../canvas/CanvasManager";
import {
  clearFabricFontCache,
  loadDocumentFonts,
} from "../../canvas/fonts/loadDocumentFonts";
import { getDocumentDesignPaperConfig } from "../../canvas/paper-config";
import { fixState } from "../../canvas/state/state-fixer";
import { useRenderContext } from "../../canvas/state/useRenderContext";
import { downloadFile } from "./helper";

export const ExportDesignRoute: React.FC = () => {
  const [params] = useSearchParams();

  const documentDesignId = params.get("document_design");
  const recordId = params.get("record");
  const reportName = params.get("report_name");

  const { data: documentDesign } = useQuery({
    enabled: !!documentDesignId,
    queryKey: ["documentDesign", documentDesignId],
    queryFn: () => fetchDocumentDesignById(documentDesignId!),
  });

  if (!documentDesign?.canvas_template || !recordId) {
    return null;
  }

  return (
    <ExportDesign
      design={documentDesign}
      recordId={recordId}
      reportName={reportName}
    />
  );
};

export interface ExportDesignProps {
  design: DocumentDesign;
  recordId: string;
  reportName?: string | null;
}

export const ExportDesign: React.FC<ExportDesignProps> = ({
  design,
  recordId,
  reportName,
}) => {
  const context = useRenderContext(
    design.canvas_template!,
    design.application,
    recordId,
    getDocumentDesignPaperConfig(design),
  );

  const normalizedState = useMemo(
    () =>
      design.canvas_template && context.application
        ? fixState(design.canvas_template, context.application)
        : undefined,
    [design.canvas_template, context.application],
  );

  const [manager, setManager] = useState<CanvasManager>();
  const [blobUrl, setBlobUrl] = useState<URL | undefined>();
  const hasExportedRef = useRef(false);

  const exportPdf = useCallback((): void => {
    if (!manager || !design) {
      return;
    }

    manager.toJsPDF(getDocumentDesignPaperConfig(design), (pdf) => {
      const blobUrl = pdf.output("bloburl");
      // if reportName passed download file
      if (reportName) {
        downloadFile(blobUrl.toString(), reportName);
      } else {
        pdf.autoPrint();
        setBlobUrl(blobUrl);
      }

      // This event is listened to in the main frontend (see frontend/projects/main/src/app/record-modal/state/service.ts - loadDocumentDesignedPdf).
      // This message is required to close the iframe window that initiates PDF file creation on the frontend.
      window.parent.postMessage(
        { type: "document-designer:pdf-download-complete" },
        "*",
      );
    });
  }, [manager, design, reportName]);

  useEffect(() => {
    if (
      context.loading ||
      hasExportedRef.current ||
      !manager ||
      !normalizedState
    ) {
      return;
    }
    hasExportedRef.current = true;

    void (async () => {
      // Load the real fonts and re-render so fabric measures with them, then
      // drop the cached fallback widths — otherwise the PDF is laid out with
      // fallback metrics and the text overflows the page.
      await loadDocumentFonts(normalizedState);
      clearFabricFontCache();
      manager.render(normalizedState, context);

      // Give async content (downloaded images, linked-record tables) time to
      // paint onto the canvas before capturing it; otherwise it comes out blank.
      setTimeout(() => {
        void manager.whenImagesLoaded().then(exportPdf);
      }, 2000);
    })();
  }, [context, manager, normalizedState, exportPdf]);

  return (
    <>
      {context.loading && <Preloader />}

      {blobUrl && (
        <iframe
          title="Export"
          src={blobUrl.toString()}
          style={{ width: "100%", height: "calc(100vh - 5px)", border: 0 }}
        />
      )}

      {!blobUrl && normalizedState && (
        <div style={{ height: 0, display: "none" }}>
          <Canvas
            state={normalizedState}
            context={context}
            isPreviewing={true}
            onStateChange={() => null}
            onSelectionChange={() => null}
            onReady={setManager}
            dimensions={getDocumentDesignPaperConfig(design)}
          />
        </div>
      )}
    </>
  );
};
